// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helperi
import { transformRoute, useTranslate } from "helpers";

// Tipovi i konstante
import { AnnualTaxSearchPageCmp, AnnualTaxSearchPageProps } from "./types";
import { AnnualTaxRow, AnnualTaxSearchFormValues } from "services/api/calculations/annualtax/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { CALCULATION_ANNUAL_TAX_CREATE_PAGE_TITLE, CALCULATION_ANNUAL_TAX_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Routes } from "ui/modules/calculation/navigation";

// API servisi
import { getAnnualTaxsAPI } from "services/api/calculations/annualtax/api";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NoResults } from "ui/components/NoResults";

// UI hooks
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Lokalne komponente
import AnnualTaxSearchFilters from "./components/AnnualTaxSearchFilters/AnnualTaxSearchFilters";
import AnnualTaxSearchResults from "./components/AnnualTaxSearchResults/AnnualTaxSearchResults";
import AnnualTaxEditCard from "../AnnualTaxEditCard/AnnualTaxEditCard";
import TabMenu from "../TabMenu/TabMenu";


const AnnualTaxSearchPage: AnnualTaxSearchPageCmp = (
  props: AnnualTaxSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<AnnualTaxRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [annualTaxId, setAnnualTaxId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getAnnualTaxsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationAnnualTaxDetailPageRouter, {
        annualTaxId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnnualTaxId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: AnnualTaxSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setAnnualTaxId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_ANNUAL_TAX_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_ANNUAL_TAX_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_ANNUAL_TAX_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <AnnualTaxSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.annualtax.search.noresults"
              )}
            />
          ) : (
            <AnnualTaxSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_ANNUAL_TAX_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <AnnualTaxEditCard
              annualTaxId={annualTaxId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

AnnualTaxSearchPage.route =
  Routes.CalculationAnnualTaxSearchPageRouter;

export default AnnualTaxSearchPage;
